import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * TokenRefreshService
 * Owns the mutable state needed to coordinate concurrent token refresh requests.
 *
 * Why a service and not module-level variables in the interceptor:
 * Module-level variables in functional interceptors are true global singletons that are
 * never reset — including across logout/login cycles. If the user logs out while a refresh
 * is in flight, `isRefreshing` stays true permanently and all subsequent requests stall.
 * Encapsulating this state here lets AuthService call reset() on logout.
 */
@Injectable({ providedIn: 'root' })
export class TokenRefreshService {
  private _isRefreshing = false;
  private readonly _subject = new BehaviorSubject<string | null>(null);

  /** Emits the new access token when a refresh completes, or null when one starts/fails. */
  readonly token$: Observable<string | null> = this._subject.asObservable();

  get isRefreshing(): boolean {
    return this._isRefreshing;
  }

  /** Call when a refresh request begins. Blocks other requests from starting their own refresh. */
  start(): void {
    this._isRefreshing = true;
    this._subject.next(null);
  }

  /** Call when a refresh succeeds. Unblocks and replays the new token to all queued requests. */
  complete(token: string): void {
    this._isRefreshing = false;
    this._subject.next(token);
  }

  /** Call when a refresh fails. Unblocks queued requests so they can propagate the error. */
  fail(): void {
    this._isRefreshing = false;
    this._subject.next(null);
  }

  /** Call on logout. Prevents stale refresh state from leaking into the next login session. */
  reset(): void {
    this._isRefreshing = false;
    this._subject.next(null);
  }
}
